export type MintleafConsoleLevel = 'log' | 'info' | 'warn' | 'error';

export type MintleafConsoleRecord = Readonly<{
  timestamp: string;
  level: MintleafConsoleLevel;
  arguments: readonly unknown[];
  stack?: string;
}>;

type ConsoleMethod = (...args: unknown[]) => void;
type ConsoleRecorderWindow = Window & {
  __MINTLEAF_CONSOLE_LOGS__?: MintleafConsoleRecord[];
  __MINTLEAF_GET_CONSOLE_LOGS__?: () => MintleafConsoleRecord[];
  __MINTLEAF_CONSOLE_RECORDER_ORIGINALS__?: Partial<Record<MintleafConsoleLevel, ConsoleMethod>>;
  __MINTLEAF_CONSOLE_RECORDER_INSTALLED__?: boolean;
};

const CONSOLE_LEVELS: MintleafConsoleLevel[] = ['log', 'info', 'warn', 'error'];
const MAX_CONSOLE_RECORDS = 500;
export const MINTLEAF_CONSOLE_RECORDER_QUERY_PARAM = 'consoleRecorder';
export const MINTLEAF_CONSOLE_RECORDER_STORAGE_KEY = 'mintleaf.consoleRecorder';

const getStack = (): string | undefined => {
  try {
    return new Error().stack;
  } catch {
    return undefined;
  }
};

export const isMintleafConsoleRecorderEnabled = (): boolean => {
  if (Boolean(import.meta.env?.DEV)) return true;
  if (typeof window === 'undefined') return false;

  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get(MINTLEAF_CONSOLE_RECORDER_QUERY_PARAM) === '1') return true;
  } catch {
    // Diagnostics must never break application bootstrap.
  }

  try {
    return window.localStorage.getItem(MINTLEAF_CONSOLE_RECORDER_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

export const installMintleafConsoleRecorder = (
  targetWindow: ConsoleRecorderWindow | undefined =
    typeof window === 'undefined' ? undefined : window
): boolean => {
  if (!targetWindow) return false;
  if (targetWindow.__MINTLEAF_CONSOLE_RECORDER_INSTALLED__) return false;

  const records = targetWindow.__MINTLEAF_CONSOLE_LOGS__ || [];
  const originals: Partial<Record<MintleafConsoleLevel, ConsoleMethod>> = {};
  const targetConsole = targetWindow.console;

  for (const level of CONSOLE_LEVELS) {
    const original = targetConsole[level].bind(targetConsole) as ConsoleMethod;
    originals[level] = original;
    targetConsole[level] = ((...args: unknown[]) => {
      records.push(Object.freeze({
        timestamp: new Date().toISOString(),
        level,
        arguments: args,
        stack: level === 'warn' || level === 'error' ? getStack() : undefined,
      }));
      if (records.length > MAX_CONSOLE_RECORDS) {
        records.splice(0, records.length - MAX_CONSOLE_RECORDS);
      }
      return original(...args);
    }) as typeof targetConsole[typeof level];
  }

  Object.defineProperty(targetWindow, '__MINTLEAF_CONSOLE_LOGS__', {
    configurable: true,
    enumerable: false,
    value: records,
    writable: true,
  });
  Object.defineProperty(targetWindow, '__MINTLEAF_GET_CONSOLE_LOGS__', {
    configurable: true,
    enumerable: false,
    value: () => records.slice(),
    writable: false,
  });
  Object.defineProperty(targetWindow, '__MINTLEAF_CONSOLE_RECORDER_ORIGINALS__', {
    configurable: true,
    enumerable: false,
    value: originals,
    writable: false,
  });
  Object.defineProperty(targetWindow, '__MINTLEAF_CONSOLE_RECORDER_INSTALLED__', {
    configurable: true,
    enumerable: false,
    value: true,
    writable: false,
  });

  return true;
};

if (isMintleafConsoleRecorderEnabled()) {
  installMintleafConsoleRecorder();
}
